import { useMemo } from "react";

import { AuthContext } from "@/data/auth/auth.context";
import { CommonModels } from "@/data/common/common.models";
import { LiveShowsQueries } from "@/data/liveShows/liveShows.queries";
import { ProfileDemoQueries } from "@/data/profileDemo/profileDemo.queries";
import { useDemoAlternativeQuery } from "@/hooks/demo/useDemoAlternativeQuery";

/**
 * Hook to determine if there's a scheduled or live liveshow with a scheduled start time.
 * Uses the same logic as LossdogLiveshowsPage to determine the top show.
 * The gradient should be displayed for both SCHEDULED and LIVE status.
 *
 * @returns Object with hasScheduledLiveshow flag and loading state
 */
export const useScheduledLiveshow = () => {
  const { isLoggedIn } = AuthContext.useAuth();
  // Use demo alternative query for past liveshows
  const { data: pastLiveshows, isFetching: isFetchingPastLiveshows } = useDemoAlternativeQuery(
    { query: LiveShowsQueries.useGetPast, params: [{ limit: 10, page: 1 }], options: { enabled: isLoggedIn } },
    { query: ProfileDemoQueries.useGetPast, params: [{ limit: 10, page: 1 }], options: { enabled: isLoggedIn } },
  );

  // Use demo alternative query for current liveshows
  const { data: currentLiveshows, isFetching: isFetchingCurrentLiveshows } = useDemoAlternativeQuery(
    { query: LiveShowsQueries.useGetCurrent, options: { enabled: isLoggedIn } },
    { query: ProfileDemoQueries.useGetCurrent, options: { enabled: isLoggedIn } },
  );

  // Use demo alternative query for upcoming liveshows
  const { data: upcomingLiveshows, isFetching: isFetchingUpcomingLiveshows } = useDemoAlternativeQuery(
    { query: LiveShowsQueries.useGetUpcoming, params: [{ limit: 10 }], options: { enabled: isLoggedIn } },
    { query: ProfileDemoQueries.useGetUpcoming, params: [{ limit: 10 }], options: { enabled: isLoggedIn } },
  );

  // Determine the top show using the same logic as autoTopShowData in LossdogLiveshowsPage
  const topShowData = useMemo(() => {
    if (!isFetchingCurrentLiveshows && currentLiveshows && currentLiveshows.length > 0) {
      return currentLiveshows[0];
    }
    if (
      !isFetchingUpcomingLiveshows &&
      upcomingLiveshows &&
      upcomingLiveshows.items &&
      upcomingLiveshows.items.length > 0
    ) {
      return upcomingLiveshows.items[0];
    }
    if (!isFetchingPastLiveshows && pastLiveshows && pastLiveshows.items && pastLiveshows.items.length > 0) {
      return pastLiveshows.items[0];
    }
    return null;
  }, [
    isFetchingCurrentLiveshows,
    currentLiveshows,
    isFetchingUpcomingLiveshows,
    upcomingLiveshows,
    isFetchingPastLiveshows,
    pastLiveshows,
  ]);

  // Check if the top show is scheduled or live (both should show the gradient)
  const hasScheduledLiveshow =
    (topShowData?.status === CommonModels.LiveShowStatusEnum.SCHEDULED ||
      topShowData?.status === CommonModels.LiveShowStatusEnum.LIVE) &&
    !!topShowData?.scheduledStartAt;

  const isFetching = isFetchingCurrentLiveshows || isFetchingPastLiveshows || isFetchingUpcomingLiveshows;

  return {
    hasScheduledLiveshow,
    isFetching,
  };
};
